// ---------------------------------------------------------------------------
// Support tickets — backend data layer.
//
// Talks to the QRiva Support API (base route: /api/support). The Support page
// component only imports the functions below, so the UI stays decoupled from
// the transport details.
//
// Routing is decided server-side from the auth token: the backend knows who the
// caller is and which tickets belong in their inbox vs. their own requests.
// `getCurrentIdentity()` is kept only for UI decisions (can this role create a
// ticket? which tab to show? which message bubble is "mine?").
// ---------------------------------------------------------------------------

import client from "../client";
import { getUserData, getOrgId } from "../../utils/authStorage";

export const TICKET_STATUSES = ["Open", "In Progress", "Resolved", "Closed"];
export const TICKET_PRIORITIES = ["Low", "Medium", "High", "Urgent"];
// NOTE: "Technical Issue", "Billing & Payments", "Transactions" and "Other" are
// confirmed accepted by the backend. The remaining three are pending backend
// confirmation — if the API rejects one it surfaces as an "Invalid category"
// error toast on submit.
export const TICKET_CATEGORIES = [
  "Technical Issue",
  "Account & Access",
  "Billing & Payments",
  "Agents & Merchants",
  "Transactions",
  "Feature Request",
  "Other",
];

export const MAX_ATTACHMENTS = 3;
export const MAX_FILE_MB = 5;

// Roles used for UI decisions and message-bubble alignment.
export const ROLE = {
  SUPER_ADMIN: "super-admin",
  ORG_ADMIN: "org-admin",
  SUBORDINATE: "subordinate",
  MOBILE_USER: "mobile-user",
};

/** Identity of the currently logged-in portal user (for UI only). */
export const getCurrentIdentity = () => {
  const data = getUserData();
  const user = data?.user || data || {};
  const userType = String(user?.user_type || user?.type || "").toLowerCase();

  let role = ROLE.SUBORDINATE;
  if (userType === "admin") role = ROLE.SUPER_ADMIN;
  else if (userType === "org") role = ROLE.ORG_ADMIN;

  return {
    role,
    name: user?.name || user?.username || "User",
    email: user?.profile?.email || user?.email || data?.email || "",
    orgId: getOrgId() || user?.orgId || user?.profile?.orgId || null,
  };
};

// ---- helpers ---------------------------------------------------------------

/** Pull a human message out of an axios error for the UI toast. */
const errMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

/** Uploaded files come back with an http:// URL; upgrade to https to avoid
 *  mixed-content blocking when the portal is served over https. */
const httpsify = (url) =>
  typeof url === "string" ? url.replace(/^http:\/\//i, "https://") : url;

const normalizeAttachment = (a) =>
  a && typeof a === "object" ? { ...a, url: httpsify(a.url) } : a;

/** Map a raw backend ticket to the shape the UI expects (adds `id`, cleans
 *  attachment URLs). Safe on both list items (no messages) and full tickets. */
const normalizeTicket = (raw) => {
  if (!raw || typeof raw !== "object") return raw;
  return {
    ...raw,
    id: raw.ticketId || raw._id || raw.id,
    messages: Array.isArray(raw.messages)
      ? raw.messages.map((m) => ({
          ...m,
          attachments: Array.isArray(m.attachments)
            ? m.attachments.map(normalizeAttachment)
            : [],
        }))
      : raw.messages,
  };
};

/** attachment objects -> the array of ids the API wants. */
const toAttachmentIds = (attachments = []) =>
  attachments.map((a) => a?.id).filter(Boolean);

// ---- attachments -----------------------------------------------------------

/**
 * Validate an image and upload it to the backend.
 * Returns { id, name, url } — `id` is sent as an attachmentId when the ticket
 * or reply is created; `url` is used for the preview/thumbnail.
 */
export const fileToAttachment = async (file) => {
  if (!file.type.startsWith("image/")) {
    throw new Error(`"${file.name}" is not an image.`);
  }
  if (file.size > MAX_FILE_MB * 1024 * 1024) {
    throw new Error(`"${file.name}" is larger than ${MAX_FILE_MB}MB.`);
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    const { data } = await client.post("/support/attachments", formData, {
      // let the browser set multipart boundary
      headers: { "Content-Type": undefined },
    });
    return {
      id: data?.id,
      name: data?.name || file.name,
      url: httpsify(data?.url),
    };
  } catch (err) {
    throw new Error(errMessage(err, `Could not upload "${file.name}".`));
  }
};

// ---- tickets ---------------------------------------------------------------

// The page filters/searches client-side, so pull a generous page.
const LIST_PARAMS = "page=1&limit=100";

/** Tickets addressed TO the current user (their inbox). */
export const getInbox = async () => {
  const { data } = await client.get(`/support/tickets/inbox?${LIST_PARAMS}`);
  return (data?.items || []).map(normalizeTicket);
};

/** Tickets the current user has raised themselves. */
export const getMyRequests = async () => {
  const { data } = await client.get(`/support/tickets/my-requests?${LIST_PARAMS}`);
  return (data?.items || []).map(normalizeTicket);
};

/** Full ticket with its conversation. */
export const getTicket = async (ticketId) => {
  const { data } = await client.get(`/support/tickets/${ticketId}`);
  return normalizeTicket(data?.data || data);
};

/** Create a new ticket. */
export const createTicket = async (
  _identity,
  { subject, category, priority, message, attachments = [] }
) => {
  try {
    const { data } = await client.post("/support/tickets", {
      subject,
      category,
      priority,
      message,
      attachmentIds: toAttachmentIds(attachments),
    });
    return normalizeTicket(data?.data || data);
  } catch (err) {
    throw new Error(errMessage(err, "Could not submit the ticket."));
  }
};

/** Append a reply, then return the refreshed ticket. */
export const replyToTicket = async (_identity, ticketId, text, attachments = []) => {
  try {
    await client.post(`/support/tickets/${ticketId}/replies`, {
      text,
      attachmentIds: toAttachmentIds(attachments),
    });
  } catch (err) {
    throw new Error(errMessage(err, "Could not post your reply."));
  }
  return getTicket(ticketId);
};

/** Change a ticket's status, then return the refreshed ticket. */
export const setTicketStatus = async (ticketId, status) => {
  try {
    await client.patch(`/support/tickets/${ticketId}/status`, { status });
  } catch (err) {
    throw new Error(errMessage(err, "Could not change the status."));
  }
  return getTicket(ticketId);
};
